import { useState } from 'react';
import { Link } from 'react-router-dom';
import QuickContact from '../contact/QuickContact';
import { useTranslation } from 'react-i18next';

const CheckoutPage = () => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    apartment: '',
    city: '',
    country: 'United States',
    state: '',
    zipCode: '',
    phone: '',
    saveInfo: false,
    shippingMethod: 'standard',
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: '',
    billingAddress: 'same',
    newsletter: false
  });

  const [errors, setErrors] = useState({});

  const [orderSummary] = useState({
    items: [
      {
        id: 1,
        title: 'Cartoon Character',
        image: '/assets/imgs/Home/Newest Comic Books/1.png',
        price: 299.99,
        quantity: 1
      }
    ],
    subtotal: 299.99,
    shipping: 0,
    tax: 24.00,
    total: 323.99
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.email) newErrors.email = t('validation.emailRequired', 'Email is required');
    if (!formData.firstName) newErrors.firstName = t('validation.firstNameRequired', 'First name is required');
    if (!formData.lastName) newErrors.lastName = t('validation.lastNameRequired', 'Last name is required');
    if (!formData.address) newErrors.address = t('validation.addressRequired', 'Address is required');
    if (!formData.city) newErrors.city = t('validation.cityRequired', 'City is required');
    if (!formData.state) newErrors.state = t('validation.stateRequired', 'State is required');
    if (!formData.zipCode) newErrors.zipCode = t('validation.zipRequired', 'ZIP code is required');
    if (formData.paymentMethod === 'card') {
      if (!formData.cardNumber) newErrors.cardNumber = t('validation.cardNumberRequired', 'Card number is required');
      if (!formData.nameOnCard) newErrors.nameOnCard = t('validation.nameOnCardRequired', 'Name on card is required');
      if (!formData.expiryDate) newErrors.expiryDate = t('validation.expiryRequired', 'Expiration date is required');
      if (!formData.cvv) newErrors.cvv = t('validation.cvvRequired', 'Security code is required');
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;
    console.log('Order submitted:', formData);
  };

  return (
    <div className="min-h-screen bg-cream pt-32">
      <div className="container mx-auto px-4 lg:px-16 pb-20">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Checkout Form */}
          <div className="lg:w-2/3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Contact Information */}
                <div>
                  <h2 className="text-lg font-bold text-text-dark mb-4">{t('checkout.contactInformation')}</h2>
                  <div className="space-y-4">
                    <label className="text-xs text-text-gray mb-1 block" htmlFor="email">{t('checkout.emailAddress')}</label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder={t('checkout.emailAddress')}
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      aria-invalid={!!errors.email}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                      required
                    />
                    {errors.email && (<p className="text-xs text-red-600">{errors.email}</p>)}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="saveInfo"
                        name="saveInfo"
                        checked={formData.saveInfo}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="saveInfo" className="text-sm text-text-gray">{t('checkout.emailNewsOffers')}</label>
                    </div>
                  </div>
                </div>

                {/* Delivery */}
                <div>
                  <h2 className="text-lg font-bold text-text-dark mb-4">{t('checkout.delivery')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs text-text-gray mb-1 block" htmlFor="firstName">{t('checkout.firstName')}</label>
                      <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      placeholder={t('checkout.firstName')}
                      value={formData.firstName}
                      onChange={handleInputChange}
                      autoComplete="given-name"
                      aria-invalid={!!errors.firstName}
                      className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                      required
                    />
                      {errors.firstName && (<p className="text-xs text-red-600">{errors.firstName}</p>)}
                    </div>
                    <div>
                      <label className="text-xs text-text-gray mb-1 block" htmlFor="lastName">{t('checkout.lastName')}</label>
                      <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      placeholder={t('checkout.lastName')}
                      value={formData.lastName}
                      onChange={handleInputChange}
                      autoComplete="family-name"
                      aria-invalid={!!errors.lastName}
                      className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                      required
                    />
                      {errors.lastName && (<p className="text-xs text-red-600">{errors.lastName}</p>)}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <label className="text-xs text-text-gray mb-1 block" htmlFor="company">{t('checkout.companyOptional')}</label>
                    <input
                      type="text"
                      name="company"
                      id="company"
                      placeholder={t('checkout.companyOptional')}
                      value={formData.company}
                      onChange={handleInputChange}
                      autoComplete="organization"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                    />
                    <label className="text-xs text-text-gray mb-1 block" htmlFor="address">{t('checkout.address')}</label>
                    <input
                      type="text"
                      name="address"
                      id="address"
                      placeholder={t('checkout.address')}
                      value={formData.address}
                      onChange={handleInputChange}
                      autoComplete="address-line1"
                      aria-invalid={!!errors.address}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                      required
                    />
                    {errors.address && (<p className="text-xs text-red-600">{errors.address}</p>)}
                    <label className="text-xs text-text-gray mb-1 block" htmlFor="apartment">{t('checkout.apartmentOptional')}</label>
                    <input
                      type="text"
                      name="apartment"
                      id="apartment"
                      placeholder={t('checkout.apartmentOptional')}
                      value={formData.apartment}
                      onChange={handleInputChange}
                      autoComplete="address-line2"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-text-gray mb-1 block" htmlFor="city">{t('checkout.city')}</label>
                        <input
                        type="text"
                        name="city"
                        id="city"
                        placeholder={t('checkout.city')}
                        value={formData.city}
                        onChange={handleInputChange}
                        autoComplete="address-level2"
                        aria-invalid={!!errors.city}
                        className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                        required
                      />
                        {errors.city && (<p className="text-xs text-red-600">{errors.city}</p>)}
                      </div>
                      <div>
                        <label className="text-xs text-text-gray mb-1 block" htmlFor="state">{t('checkout.state')}</label>
                        <select
                        name="state"
                        id="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        autoComplete="address-level1"
                        aria-invalid={!!errors.state}
                        className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                        required
                      >
                        <option value="">{t('checkout.state')}</option>
                        <option value="CA">California</option>
                        <option value="NY">New York</option>
                        <option value="TX">Texas</option>
                        <option value="FL">Florida</option>
                      </select>
                        {errors.state && (<p className="text-xs text-red-600">{errors.state}</p>)}
                      </div>
                      <div>
                        <label className="text-xs text-text-gray mb-1 block" htmlFor="zipCode">{t('checkout.zipCode')}</label>
                        <input
                        type="text"
                        name="zipCode"
                        id="zipCode"
                        placeholder={t('checkout.zipCode')}
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        autoComplete="postal-code"
                        aria-invalid={!!errors.zipCode}
                        className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                        required
                      />
                        {errors.zipCode && (<p className="text-xs text-red-600">{errors.zipCode}</p>)}
                      </div>
                    </div>
                    
                    <label className="text-xs text-text-gray mb-1 block" htmlFor="phone">{t('checkout.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      placeholder={t('checkout.phone')}
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                    />
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="saveInfoDelivery"
                        name="saveInfoDelivery"
                        className="mr-2"
                      />
                      <label htmlFor="saveInfoDelivery" className="text-sm text-text-gray">{t('checkout.saveInfoNext')}</label>
                    </div>
                  </div>
                </div>

                {/* Shipping Method */}
                <div>
                  <h2 className="text-lg font-bold text-text-dark mb-4">{t('checkout.shippingMethod')}</h2>
                  <div className="border border-gray-300 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="standard"
                          name="shippingMethod"
                          value="standard"
                          checked={formData.shippingMethod === 'standard'}
                          onChange={handleInputChange}
                          className="mr-3"
                        />
                        <label htmlFor="standard" className="text-sm text-text-dark">{t('checkout.standard')}</label>
                      </div>
                      <span className="text-sm font-bold text-text-dark">{t('checkout.free')}</span>
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div>
                  <h2 className="text-lg font-bold text-text-dark mb-4">{t('checkout.payment')}</h2>
                  <p className="text-sm text-text-gray mb-4">{t('checkout.secureEncrypted')}</p>
                  
                  <div className="border border-gray-300 rounded-md">
                    <div className="p-4 border-b border-gray-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="card"
                            name="paymentMethod"
                            value="card"
                            checked={formData.paymentMethod === 'card'}
                            onChange={handleInputChange}
                            className="mr-3"
                          />
                          <label htmlFor="card" className="text-sm text-text-dark">{t('checkout.creditCard')}</label>
                        </div>
                        <div className="flex gap-2">
                          <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center">
                            VISA
                          </div>
                          <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center">
                            MC
                          </div>
                          <div className="w-8 h-5 bg-blue-800 rounded text-white text-xs flex items-center justify-center">
                            AMEX
                          </div>
                        </div>
                      </div>
                      
                      {formData.paymentMethod === 'card' && (
                        <div className="mt-4 space-y-4">
                          <label className="text-xs text-text-gray mb-1 block" htmlFor="cardNumber">{t('checkout.cardNumber')}</label>
                          <input
                            type="text"
                            name="cardNumber"
                            id="cardNumber"
                            placeholder={t('checkout.cardNumber')}
                            value={formData.cardNumber}
                            onChange={handleInputChange}
                            autoComplete="cc-number"
                            aria-invalid={!!errors.cardNumber}
                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                            required
                          />
                          {errors.cardNumber && (<p className="text-xs text-red-600">{errors.cardNumber}</p>)}
                          <label className="text-xs text-text-gray mb-1 block" htmlFor="nameOnCard">{t('checkout.nameOnCard')}</label>
                          <input
                            type="text"
                            name="nameOnCard"
                            id="nameOnCard"
                            placeholder={t('checkout.nameOnCard')}
                            value={formData.nameOnCard}
                            onChange={handleInputChange}
                            autoComplete="cc-name"
                            aria-invalid={!!errors.nameOnCard}
                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                            required
                          />
                          {errors.nameOnCard && (<p className="text-xs text-red-600">{errors.nameOnCard}</p>)}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs text-text-gray mb-1 block" htmlFor="expiryDate">{t('checkout.expiry')}</label>
                              <input
                              type="text"
                              name="expiryDate"
                              id="expiryDate"
                              placeholder={t('checkout.expiry')}
                              value={formData.expiryDate}
                              onChange={handleInputChange}
                              autoComplete="cc-exp"
                              aria-invalid={!!errors.expiryDate}
                              className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                              required
                            />
                              {errors.expiryDate && (<p className="text-xs text-red-600">{errors.expiryDate}</p>)}
                            </div>
                            <div>
                              <label className="text-xs text-text-gray mb-1 block" htmlFor="cvv">{t('checkout.securityCode')}</label>
                              <input
                              type="text"
                              name="cvv"
                              id="cvv"
                              placeholder={t('checkout.securityCode')}
                              value={formData.cvv}
                              onChange={handleInputChange}
                              autoComplete="cc-csc"
                              aria-invalid={!!errors.cvv}
                              className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-gold text-sm"
                              required
                            />
                              {errors.cvv && (<p className="text-xs text-red-600">{errors.cvv}</p>)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <label htmlFor="newsletter" className="text-sm text-text-gray">{t('checkout.useShippingAsBilling')}</label>
                  </div>
                </div>

                {/* Complete Order Button */}
                <div className="pb-8">
                  <button
                    type="submit"
                    className="w-full bg-brown text-cream py-4 rounded-md text-sm font-medium uppercase tracking-wide hover:bg-brown-dark transition-colors"
                  >
                    {t('checkout.completeOrder')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold text-text-dark mb-4">{t('checkout.orderSummary')}</h2>
              
              {/* Product */}
              <div className="flex items-center gap-4 mb-6 p-4 border border-gray-200 rounded-md">
                <div className="w-16 h-20 bg-[#e8dcc8] border border-text-dark rounded overflow-hidden flex-shrink-0 relative">
                  <img 
                    src={orderSummary.items[0].image} 
                    alt={orderSummary.items[0].title}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-text-gray text-white text-xs rounded-full flex items-center justify-center">
                    1
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-text-dark text-sm">{orderSummary.items[0].title}</h3>
                </div>
                <div className="text-right">
                  <p className="font-bold text-text-dark">${orderSummary.items[0].price}</p>
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('checkout.subtotal')}</span>
                  <span className="text-text-dark">${orderSummary.subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('checkout.tax')}</span>
                  <span className="text-text-dark">${orderSummary.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('checkout.shipping')}</span>
                  <span className="text-green-600">{t('checkout.free')}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between font-bold text-lg">
                  <span className="text-text-dark">{t('checkout.total')}</span>
                  <span className="text-gold">${orderSummary.total}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuickContact />
    </div>
  );
};

export default CheckoutPage;
